import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useStore } from '../store';
import type { Goal, Game, ShotType, GameSituation, GameOutcome } from '../types';
import GoalDiagram from '../components/GoalDiagram';
import ShotPositionDiagram from '../components/ShotPositionDiagram';
import LiveShotsCounter from '../components/LiveShotsCounter';
import ShareDialog from '../components/ShareDialog';
import SavesList from '../components/SavesList';
import AITrainerButton from '../components/AITrainerButton';
import {
  Pencil,
  Save,
  Trash2,
  X,
  PlusCircle,
  AlertCircle,
  FileDown,
  Share2,
  Loader,
  Brain,
  Lock,
  Video,
  Upload,
  Sparkles,
  Play,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import GamePDF from '../components/GamePDF';
import { useRipple } from '../lib/useRipple';
import { videoAnalysisService } from '../services/videoAnalysisService';
import { VideoUploadService } from '../services/videoUploadService';
import ShotAnalysisCard from '../components/ShotAnalysisCard';
import VideoPlayer, { VideoPlayerRef } from '../components/VideoPlayer';
import { useTranslation } from '../i18n/useTranslation';

interface GameFormData {
  date: string;
  opponent: string;
  competitionType: Game['competitionType'];
  shotsFaced: string;
  saves: string;
  outcome: GameOutcome;
}

const getOutcomeColor = (outcome: GameOutcome): string => {
  switch (outcome) {
    case 'win':
    case 'win-ot':
    case 'win-so':
      return 'text-green-600';
    case 'loss':
    case 'loss-ot':
    case 'loss-so':
      return 'text-red-600';
    default:
      return 'text-gray-900';
  }
};

const formatOutcome = (outcome: GameOutcome): string => {
  if (!outcome) return '—';
  return outcome.toUpperCase().replace('-', ' ');
};

export default function GameDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  const language = useStore((state) => state.language);
  const setShowUpgradeModal = useStore((state) => state.setShowUpgradeModal);
  const { t } = useTranslation();
  const [game, setGame] = useState<Game | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [goalToDelete, setGoalToDelete] = useState<Goal | null>(null);
  const [showDeleteGameModal, setShowDeleteGameModal] = useState(false);
  const [provisionalPosition, setProvisionalPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [provisionalShotPosition, setProvisionalShotPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [editingGame, setEditingGame] = useState(false);
  const [showShotsCounter, setShowShotsCounter] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showShotTypeModal, setShowShotTypeModal] = useState(false);
  const [showGameSituationModal, setShowGameSituationModal] = useState(false);
  
  // Video upload state for goal creation
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoAnalyzing, setVideoAnalyzing] = useState(false);
  const [videoError, setVideoError] = useState('');
  
  // Video input method state
  const [videoInputMethod, setVideoInputMethod] = useState<'url' | 'file'>('url');
  
  // Snackbar notification state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // AI Analytics expandable state
  const [showAIAnalytics, setShowAIAnalytics] = useState(true);
  // Video player expandable state
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [deletingGoal, setDeletingGoal] = useState(false);
  const videoPlayerRef = useRef<VideoPlayerRef>(null);

  const [gameFormData, setGameFormData] = useState<GameFormData>({
    date: '',
    opponent: '',
    competitionType: 'regular-season',
    shotsFaced: '',
    saves: '',
    outcome: null,
  });

  const [formData, setFormData] = useState({
    title: '',
    xPosition: 50,
    yPosition: 50,
    shotXPosition: null as number | null,
    shotYPosition: null as number | null,
    shotType: '' as ShotType,
    gameSituation: '' as GameSituation,
    situationDescription: '',
    result: 'goal' as 'save' | 'goal',
    status: 'completed' as 'draft' | 'completed',
    // AI Analysis fields (optional for manually created goals)
    clipStartTime: '' as string,
    clipEndTime: '' as string,
    analysis: undefined as {
      goalieTechnique: string;
      whatWentWell: string;
      areasForImprovement: string;
      keyMoment: string;
      coachingTip: string;
    } | undefined,
    videoUrl: '' as string,
    isAIGenerated: false as boolean,
  });

  const getUsageInfo = useStore((state) => state.getUsageInfo);
  const usageInfo = getUsageInfo('analysis');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [goalSaving, setGoalSaving] = useState(false);
  const { createRipple } = useRipple();

  // Snackbar function
  const showSnackbarMessage = (message: string) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 4000); // Hide after 4 seconds
  };

  useEffect(() => {
    const fetchGameAndGoals = async () => {
      if (!id || !user) {
        setError(t('messages.unauthorizedAccess'));
        setLoading(false);
        return;
      }

      try {
        const gameDoc = await getDoc(doc(db, 'games', id));

        if (!gameDoc.exists()) {
          setError(t('messages.gameNotFound'));
          setLoading(false);
          return;
        }

        const gameData = {
          id: gameDoc.id,
          ...gameDoc.data(),
          date: new Date(gameDoc.data().date),
          createdAt: new Date(gameDoc.data().createdAt),
        } as Game;

        if (gameData.userId !== user.uid) {
          setError(t('messages.unauthorizedAccess'));
          setLoading(false);
          return;
        }

        setGame(gameData);
        setGameFormData({
          date: gameData.date.toISOString().split('T')[0],
          opponent: gameData.opponent,
          competitionType: gameData.competitionType,
          shotsFaced: gameData.shotsFaced?.toString() || '',
          saves: gameData.saves?.toString() || '',
          outcome: gameData.outcome,
        });

        // Fetch goals without orderBy to avoid composite index requirement
        const goalsQuery = query(
          collection(db, 'goals'),
          where('gameId', '==', id)
        );

        const goalsSnapshot = await getDocs(goalsQuery);
        const goalsData = goalsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        })) as Goal[];

        // Sort goals by createdAt in ascending order (first added = first shown)
        goalsData.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

        setGoals(goalsData);
        setError('');
      } catch (err) {
        console.error('Error fetching game details:', err);
        setError(t('messages.loadingError'));
      } finally {
        setLoading(false);
      }
    };

    fetchGameAndGoals();
  }, [id, user]);

  const handleGameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !game) return;

    try {
      const gameDate = new Date(gameFormData.date);

      if (isNaN(gameDate.getTime())) {
        setError(t('game.validation.pleaseEnterValidDate'));
        return;
      }

      const shotsFacedNum =
        gameFormData.shotsFaced && parseInt(gameFormData.shotsFaced) > 0
          ? parseInt(gameFormData.shotsFaced)
          : null;

      const saves =
        shotsFacedNum !== null ? shotsFacedNum - goals.length : null;

      const updatedGameData = {
        date: gameDate.getTime(),
        opponent: gameFormData.opponent,
        competitionType: gameFormData.competitionType,
        shotsFaced: shotsFacedNum,
        saves,
        outcome: gameFormData.outcome,
        updatedAt: Date.now(),
      };

      await updateDoc(doc(db, 'games', id), updatedGameData);

      setGame({
        ...game,
        ...updatedGameData,
        date: gameDate,
      });

      setEditingGame(false);
      setError('');
    } catch (err) {
      console.error('Error updating game:', err);
      setError(t('messages.saveError'));
    }
  };

  const handleSaveShots = async (
    totalShots: number,
    periodShots: Game['periodShots']
  ) => {
    if (!id || !game) return;

    try {
      const shotsFaced = totalShots > 0 ? totalShots : null;
      const saves = shotsFaced !== null ? shotsFaced - goals.length : null;

      const gameRef = doc(db, 'games', id);
      await updateDoc(gameRef, {
        shotsFaced,
        saves,
        periodShots: shotsFaced !== null ? periodShots : null,
      });

      setGame((prev) =>
        prev
          ? {
              ...prev,
              shotsFaced,
              saves,
              periodShots: shotsFaced !== null ? periodShots : null,
            }
          : null
      );

      setShowShotsCounter(false);
    } catch (err) {
      console.error('Error updating shots:', err);
      setError(t('messages.saveError'));
    }
  };

  const handleGoalPositionSelect = (x: number, y: number) => {
    setProvisionalPosition({ x, y });
    setFormData((prev) => ({
      ...prev,
      xPosition: x,
      yPosition: y,
    }));
  };

  const handleShotPositionSelect = (x: number, y: number) => {
    setProvisionalShotPosition({ x, y });
    setFormData((prev) => ({
      ...prev,
      shotXPosition: x,
      shotYPosition: y,
    }));
  };

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !game) return;

    setGoalSaving(true);
    try {
      const now = new Date();
      if (editingGoal) {
        // PATCH approach - only update changed fields
        const updateData: any = {
          updatedAt: serverTimestamp(),
        };

        // Only include fields that have changed
        if (formData.title !== editingGoal.title) updateData.title = formData.title;
        if (formData.xPosition !== editingGoal.xPosition) updateData.xPosition = formData.xPosition;
        if (formData.yPosition !== editingGoal.yPosition) updateData.yPosition = formData.yPosition;
        if (formData.shotXPosition !== editingGoal.shotXPosition) updateData.shotXPosition = formData.shotXPosition;
        if (formData.shotYPosition !== editingGoal.shotYPosition) updateData.shotYPosition = formData.shotYPosition;
        if (formData.shotType !== editingGoal.shotType) updateData.shotType = formData.shotType;
        if (formData.gameSituation !== editingGoal.gameSituation) updateData.gameSituation = formData.gameSituation;
        if (formData.situationDescription !== editingGoal.situationDescription) updateData.situationDescription = formData.situationDescription;
        if (formData.result !== editingGoal.result) updateData.result = formData.result;
        if (formData.status !== editingGoal.status) updateData.status = formData.status;

        await updateDoc(doc(db, 'goals', editingGoal.id), updateData);
      } else {
        // For new goals, create complete object
        const goalData = {
          ...formData,
          gameId: id,
          userId: user?.uid || '',
          status: 'completed' as const,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Remove undefined fields to avoid Firestore errors
        Object.keys(goalData).forEach(key => {
          if (goalData[key as keyof typeof goalData] === undefined) {
            delete goalData[key as keyof typeof goalData];
          }
        });

        await addDoc(collection(db, 'goals'), goalData);
      }

      // Refresh goals without orderBy to avoid composite index requirement
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', id)
      );

      const goalsSnapshot = await getDocs(goalsQuery);
      const goalsData = goalsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || Date.now()),
      })) as Goal[];

      // Sort goals by createdAt in ascending order on the client side
      goalsData.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

      setGoals(goalsData);

      if (game.shotsFaced !== null && game.shotsFaced > 0) {
        const saves = game.shotsFaced - goalsData.length;
        await updateDoc(doc(db, 'games', id), { saves });
        setGame((prev) => (prev ? { ...prev, saves } : null));
      }

      // Show success message
      showSnackbarMessage(t('messages.goalSaved'));

      setShowGoalForm(false);
      setEditingGoal(null);
      setProvisionalPosition(null);
      setProvisionalShotPosition(null);
      setFormData({
        title: '',
        xPosition: 50,
        yPosition: 50,
        shotXPosition: null,
        shotYPosition: null,
        shotType: '' as ShotType,
        gameSituation: '' as GameSituation,
        situationDescription: '',
        result: 'goal',
        status: 'completed',
        clipStartTime: '',
        clipEndTime: '',
        analysis: undefined,
        videoUrl: '',
        isAIGenerated: false,
      });
    } catch (err) {
      console.error('Error saving goal:', err);
      setError(t('messages.saveError'));
    } finally {
      setGoalSaving(false);
    }
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      xPosition: goal.xPosition,
      yPosition: goal.yPosition,
      shotXPosition: goal.shotXPosition,
      shotYPosition: goal.shotYPosition,
      shotType: goal.shotType || ('' as ShotType),
      gameSituation: goal.gameSituation || ('' as GameSituation),
      situationDescription: goal.situationDescription,
      result: goal.result || 'goal',
      status: goal.status || 'completed',
      clipStartTime: goal.clipStartTime || '',
      clipEndTime: goal.clipEndTime || '',
      analysis: goal.analysis,
      videoUrl: goal.videoUrl || '',
      isAIGenerated: goal.isAIGenerated || false,
    });
    setProvisionalPosition({ x: goal.xPosition, y: goal.yPosition });
    if (goal.shotXPosition !== null && goal.shotYPosition !== null) {
      setProvisionalShotPosition({
        x: goal.shotXPosition,
        y: goal.shotYPosition,
      });
    }
    setShowGoalForm(true);
  };

  const handleDeleteGoal = async (goalId: string) => {
    setDeletingGoal(true);
    try {
      // Get goal data before deleting to check for video
      const goalRef = doc(db, 'goals', goalId);
      const goalDoc = await getDoc(goalRef);
      const goalData = goalDoc.data();
      
      // Delete video from Firebase Storage if it exists
      if (goalData?.videoUrl && goalData?.isAIGenerated) {
        try {
          console.log('Attempting to delete video:', goalData.videoUrl);
          
          // Extract file path from Firebase Storage URL
          const url = new URL(goalData.videoUrl);
          console.log('Parsed URL:', url.pathname);
          
          // Firebase Storage URLs have format: /v0/b/{bucket}/o/{filePath}
          const pathMatch = url.pathname.match(/\/o\/(.+)(?:\?|$)/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            console.log('Extracted file path:', filePath);
            
            const videoRef = ref(storage, filePath);
            await deleteObject(videoRef);
            console.log('Video successfully deleted from storage:', filePath);
          } else {
            console.warn('Could not extract file path from URL:', goalData.videoUrl);
          }
        } catch (videoError: any) {
          // Don't log as error if file doesn't exist - it's already gone
          if (videoError?.code === 'storage/object-not-found') {
            console.log('Video file already deleted or does not exist:', goalData.videoUrl);
          } else {
            console.error('Failed to delete video from storage:', videoError);
          }
          // Continue with goal deletion even if video deletion fails
        }
      } else {
        console.log('No video to delete - videoUrl:', goalData?.videoUrl, 'isAIGenerated:', goalData?.isAIGenerated);
      }
      
      // Delete the goal document
      await deleteDoc(goalRef);

      // Refresh goals without orderBy to avoid composite index requirement
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', id)
      );

      const goalsSnapshot = await getDocs(goalsQuery);
      const goalsData = goalsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || Date.now()),
      })) as Goal[];

      // Sort goals by createdAt in ascending order on the client side
      goalsData.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

      setGoals(goalsData);

      if (game && game.shotsFaced !== null && game.shotsFaced > 0) {
        const saves = game.shotsFaced - goalsData.length;
        await updateDoc(doc(db, 'games', id), { saves });
        setGame((prev) => (prev ? { ...prev, saves } : null));
      }

      setGoalToDelete(null);
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError(t('messages.deleteError'));
    } finally {
      setDeletingGoal(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!id) return;

    try {
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', id)
      );
      const goalsSnapshot = await getDocs(goalsQuery);
      const deletePromises = goalsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      await deleteDoc(doc(db, 'games', id));

      navigate('/dashboard');
    } catch (err) {
      console.error('Error deleting game:', err);
      setError(t('messages.deleteError'));
    }
  };

  const getPdfFilename = () => {
    if (!game) return 'game-report.pdf';
    const timestamp = game.date.toISOString().split('T')[0];
    const opponent = game.opponent.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `game-report-${opponent}-${timestamp}.pdf`;
  };


  const handleVideoAnalysis = async () => {
    // Validate input
    if (!videoUrl && !videoFile) {
      setVideoError(t('game.validation.pleaseProvideVideoUrl'));
      return;
    }

    setVideoError('');
    let finalVideoUrl = '';

    try {
      setVideoUploading(true);
      
      if (videoFile) {
        // Upload file to Firebase Storage
        const uploadResult = await VideoUploadService.uploadVideo(videoFile, id!, user?.uid || '');
        finalVideoUrl = uploadResult.url;
      } else if (videoUrl) {
        // Validate URL first
        const urlError = VideoUploadService.validateVideoUrl(videoUrl);
        if (urlError) {
          setVideoError(urlError);
          return;
        }
        
        // Download video from URL and upload to Firebase Storage
        const response = await fetch(videoUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch video: ${response.status}`);
        }
        
        const blob = await response.blob();
        const file = new File([blob], 'video.mp4', { type: blob.type });
        const uploadResult = await VideoUploadService.uploadVideo(file, id!, user?.uid || '');
        finalVideoUrl = uploadResult.url;
      }

      setVideoUploading(false);
      
      // Start AI analysis
      setVideoAnalyzing(true);

      // Analyze video with AI - always use the original source before Firebase upload
      let analysisResult;
      if (videoFile) {
        // For uploaded files, create a blob URL for analysis
        const blobUrl = URL.createObjectURL(videoFile);
        analysisResult = await videoAnalysisService.analyzeVideo(blobUrl, language);
        URL.revokeObjectURL(blobUrl); // Clean up blob URL
      } else {
        // For URL inputs, use the original URL directly (not the Firebase Storage URL)
        analysisResult = await videoAnalysisService.analyzeVideo(videoUrl, language);
      }
      
      if (analysisResult && analysisResult.shots && analysisResult.shots.length > 0) {
        // Create completed goals for each shot detected
        const goalPromises = analysisResult.shots.map(async (shot, index) => {
          const goalData = {
            gameId: id!,
            userId: user?.uid || '',
            title: shot.shotType,
            xPosition: shot.shotDestinationX,
            yPosition: shot.shotDestinationY,
            shotXPosition: shot.shotOriginX,
            shotYPosition: shot.shotOriginY,
            situationDescription: shot.analysis.keyMoment,
            shotType: shot.shotType,
            gameSituation: shot.gameSituation,
            result: shot.result,
            status: 'completed' as const,
            clipStartTime: shot.clipStartTime,
            clipEndTime: shot.clipEndTime,
            analysis: shot.analysis,
            videoUrl: finalVideoUrl,
            isAIGenerated: true,
            createdAt: serverTimestamp()
          };

          // Remove undefined fields to avoid Firestore errors
          Object.keys(goalData).forEach(key => {
            if (goalData[key as keyof typeof goalData] === undefined) {
              delete goalData[key as keyof typeof goalData];
            }
          });
          
          return addDoc(collection(db, 'goals'), goalData);
        });

        // Wait for all goals to be created
        await Promise.all(goalPromises);
        
        // Clear video form inputs
        setVideoUrl('');
        setVideoFile(null);
        setVideoError('');

        // Show success message
        showSnackbarMessage(t('notifications.videoAnalysisComplete', { count: analysisResult.shots.length }));
        
        // Refresh goals to show the new AI-generated goals
        const goalsQuery = query(
          collection(db, 'goals'),
          where('gameId', '==', id!)
        );

        const goalsSnapshot = await getDocs(goalsQuery);
        const goalsData = goalsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        })) as Goal[];

        // Sort goals by createdAt in ascending order (first added = first shown)
        goalsData.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        setGoals(goalsData);
      }
    } catch (error) {
      console.error('Video analysis error:', error);
      setVideoError(error instanceof Error ? error.message : t('messages.analysisError'));
    } finally {
      setVideoUploading(false);
      setVideoAnalyzing(false);
    }
  };

  const parseTimeToSeconds = (timeString: string): number => {
    const [minutes, seconds] = timeString.split(':').map(Number);
    const totalSeconds = minutes * 60 + seconds;
    console.log(`Converting ${timeString} to ${totalSeconds} seconds`);
    return totalSeconds;
  };

  const showClip = (clipStartTime: string, clipEndTime: string) => {
    const startSeconds = parseTimeToSeconds(clipStartTime);
    const endSeconds = parseTimeToSeconds(clipEndTime);
    
    console.log(`Playing clip: ${clipStartTime}-${clipEndTime} (${startSeconds}s-${endSeconds}s)`);
    
    if (!formData.videoUrl) {
      showSnackbarMessage('Video not available');
      return;
    }
    
    // Show video player if it's hidden, then play clip after a brief delay
    if (!showVideoPlayer) {
      setShowVideoPlayer(true);
      // Wait for video player to render before trying to play clip
      setTimeout(() => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.playClip(startSeconds, endSeconds);
        } else {
          showSnackbarMessage('Video player not ready, please try again');
        }
      }, 300); // Give time for video player to mount
    } else {
      // Video player is already visible, play immediately
      if (videoPlayerRef.current) {
        videoPlayerRef.current.playClip(startSeconds, endSeconds);
      } else {
        showSnackbarMessage('Video player not ready, please try again');
      }
    }
  };


  const handleUpgrade = async () => {
    if (!user) return;
    setShowUpgradeModal(true);
  };

  if (loading) {
    return (
      <div className='max-w-6xl mx-auto px-4 py-8'>
        <div className='flex items-center justify-center min-h-[400px]'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1b3968]'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='max-w-6xl mx-auto px-4 py-8'>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 flex items-center'>
          <AlertCircle className='h-5 w-5 text-red-500 mr-2' />
          <p className='text-red-700'>{error}</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className='max-w-6xl mx-auto px-4 py-8'>
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
          <p className='text-yellow-700'>Game not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto px-4 pb-8'>
      {/* AI Analysis Limit warning and Upgrade button - now above the header */}
      {usageInfo && !usageInfo.allowed && (
        <div className='mb-6 w-full'>
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 mb-2'>
            <div className='flex items-start'>
              <Lock className='h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <h3 className='text-sm font-medium text-amber-800'>
                  AI Analysis Limit Reached
                </h3>
                <p className='text-sm text-amber-700 mt-1'>
                  You've used {usageInfo.usage?.used || 0} of{' '}
                  {usageInfo.usage?.limit || 0} free AI analyses. Upgrade to get
                  unlimited access.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            disabled={upgradeLoading}
            className='inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors bg-[#1b3968] text-white hover:bg-[#1b3968]/90 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {upgradeLoading ? (
              <Loader className='h-5 w-5 animate-spin' />
            ) : (
              <Brain className='h-5 w-5' />
            )}
            <span>
              {upgradeLoading ? t('common.loading') : t('gameDetails.upgradeForAIAnalysis')}
            </span>
          </button>
        </div>
      )}
      {/* Header Section */}
      <div className='glass-card p-6 mb-6 md:mb-8'>
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold text-primary-600 mb-2 italic'>
              {t('game.title')}
            </h1>
            <p className='text-muted'>
              {t('gameDetails.analyzeGamePerformance')}
            </p>
          </div>

          <div className='flex flex-col md:flex-row md:items-center gap-4'>
            {/* AI Trainer button - appears first on mobile */}
            {(!usageInfo || usageInfo.allowed) && (
              <div className='flex justify-center md:justify-start'>
                <AITrainerButton
                  gameIds={[id!]}
                  games={[game]}
                  goals={goals}
                  variant='primary'
                />
              </div>
            )}
            {/* Export and Share buttons - appear below AI Trainer on mobile, inline on desktop */}
            {game && (
              <div className='flex items-center gap-3 justify-start'>
                <PDFDownloadLink
                  document={<GamePDF game={game} goals={goals} />}
                  fileName={getPdfFilename()}
                  className='btn-mui-secondary btn-ripple group'
                  onClick={createRipple}
                >
                  <FileDown className='h-5 w-5' />
                  <span>{t('gameDetails.exportPDF')}</span>
                </PDFDownloadLink>
                <button
                  onClick={(e) => {
                    createRipple(e);
                    setShowShareDialog(true);
                  }}
                  className='btn-mui-secondary btn-ripple group'
                >
                  <Share2 className='h-5 w-5' />
                  <span className='hidden sm:inline'>{t('gameDetails.share')}</span>
                </button>
                <button
                  onClick={(e) => {
                    createRipple(e);
                    setEditingGame(!editingGame);
                  }}
                  className='btn-mui-secondary btn-ripple group'
                  title={editingGame ? t('gameDetails.cancelEditing') : t('gameDetails.editGame')}
                >
                  {editingGame ? (
                    <X className='h-5 w-5' />
                  ) : (
                    <Pencil className='h-5 w-5' />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    createRipple(e);
                    setShowDeleteGameModal(true);
                  }}
                  className='text-red-500 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition-colors'
                  title={t('gameDetails.deleteGame')}
                >
                  <Trash2 className='h-5 w-5' />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='glass-card p-6 mb-8'>
        {editingGame ? (
          <form onSubmit={handleGameSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('game.date')}
                </label>
                <input
                  type='date'
                  value={gameFormData.date}
                  onChange={(e) =>
                    setGameFormData({ ...gameFormData, date: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('game.opponent')}
                </label>
                <input
                  type='text'
                  value={gameFormData.opponent}
                  onChange={(e) =>
                    setGameFormData({
                      ...gameFormData,
                      opponent: e.target.value,
                    })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('game.competitionType')}
                </label>
                <select
                  value={gameFormData.competitionType}
                  onChange={(e) =>
                    setGameFormData({
                      ...gameFormData,
                      competitionType: e.target
                        .value as Game['competitionType'],
                    })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                  required
                >
                  <option value='regular-season'>{t('game.competitionTypes.regular-season')}</option>
                  <option value='playoff'>{t('game.competitionTypes.playoff')}</option>
                  <option value='tournament'>{t('game.competitionTypes.tournament')}</option>
                  <option value='exhibition'>{t('game.competitionTypes.exhibition')}</option>
                  <option value='practice'>{t('game.competitionTypes.practice')}</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('gameDetails.shotsFaced')}
                </label>
                <input
                  type='number'
                  value={gameFormData.shotsFaced}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGameFormData({
                      ...gameFormData,
                      shotsFaced: value,
                      saves:
                        value && parseInt(value) > 0
                          ? (parseInt(value) - goals.length).toString()
                          : '',
                    });
                  }}
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                  min='0'
                />
                <p className='mt-1 text-sm text-gray-500'>
                  {t('gameDetails.savesCalculatedAutomatically')}
                </p>
              </div>
              <div>
                <label
                  htmlFor='outcome'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('gameDetails.gameOutcome')}
                </label>
                <select
                  id='outcome'
                  value={gameFormData.outcome || ''}
                  onChange={(e) =>
                    setGameFormData({
                      ...gameFormData,
                      outcome: e.target.value as GameOutcome,
                    })
                  }
                  className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                >
                  <option value=''>{t('gameDetails.selectOutcome')}</option>
                  <option value='win'>{t('game.outcomes.win')}</option>
                  <option value='loss'>{t('game.outcomes.loss')}</option>
                  <option value='win-ot'>{t('game.outcomes.win-ot')}</option>
                  <option value='loss-ot'>{t('game.outcomes.loss-ot')}</option>
                  <option value='win-so'>{t('game.outcomes.win-so')}</option>
                  <option value='loss-so'>{t('game.outcomes.loss-so')}</option>
                </select>
              </div>
            </div>
            <div className='flex justify-end space-x-3'>
              <button
                type='button'
                onClick={() => {
                  setEditingGame(false);
                  setGameFormData({
                    date: game.date.toISOString().split('T')[0],
                    opponent: game.opponent,
                    competitionType: game.competitionType,
                    shotsFaced: game.shotsFaced?.toString() || '',
                    saves: game.saves?.toString() || '',
                    outcome: game.outcome,
                  });
                }}
                className='px-4 py-2 text-gray-700 hover:text-gray-900'
              >
                {t('common.cancel')}
              </button>
              <button
                type='submit'
                className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center'
              >
                <Save className='h-4 w-4 mr-2' />
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('game.date')}</h3>
              <p className='mt-1 text-lg text-gray-900'>
                {game.date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('game.opponent')}</h3>
              <p className='mt-1 text-lg text-gray-900'>{game.opponent}</p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('game.competitionType')}</h3>
              <p className='mt-1 text-lg text-gray-900'>
                {game.competitionType}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>
                {t('game.savePercentage')}
              </h3>
              <p className='mt-1 text-lg text-gray-900'>
                {game.shotsFaced !== null && game.saves !== null
                  ? `${((game.saves / game.shotsFaced) * 100).toFixed(1)}%`
                  : '—'}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('game.outcome')}</h3>
              <p
                className={`mt-1 text-lg font-medium ${getOutcomeColor(
                  game.outcome
                )}`}
              >
                {formatOutcome(game.outcome)}
              </p>
            </div>
          </div>
        )}
      </div>



      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <div className='glass-card p-6'>
          <div className='flex justify-between items-start'>
            <div>
              <h3 className='text-lg font-medium text-gray-900 mb-2'>{t('gameDetails.shots')}</h3>
              <div className='text-3xl font-bold text-[#1b3968]'>
                {!game.shotsFaced || game.shotsFaced === 0
                  ? '—'
                  : game.shotsFaced}
              </div>
              <p className='text-sm text-gray-500'>{t('gameDetails.totalShotsFaced')}</p>
            </div>
            <button
              onClick={() => setShowShotsCounter(true)}
              className='text-[#1b3968] hover:text-[#1b3968]/80 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-sm font-medium'
            >
              {t('liveShotsCounter.title')}
            </button>
          </div>
          {game.periodShots && game.shotsFaced > 0 && (
            <div className='mt-4 pt-4 border-t border-gray-100'>
              <div className='space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Period 1:</span>
                  <span className='font-medium'>
                    {game.periodShots.period1}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Period 2:</span>
                  <span className='font-medium'>
                    {game.periodShots.period2}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Period 3:</span>
                  <span className='font-medium'>
                    {game.periodShots.period3}
                  </span>
                </div>
                {game.periodShots.overtime > 0 && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Overtime:</span>
                    <span className='font-medium'>
                      {game.periodShots.overtime}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className='bg-white shadow rounded-lg p-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>{t('gameDetails.saves')}</h3>
          <div className='text-3xl font-bold text-green-600'>
            {!game.saves || game.saves === 0 ? '—' : game.saves}
          </div>
          <p className='text-sm text-gray-500'>{t('gameDetails.totalSavesMade')}</p>
        </div>
        <div className='bg-white shadow rounded-lg p-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            {t('gameDetails.goalsAgainst')}
          </h3>
          <div className='text-3xl font-bold text-red-600'>{goals.length}</div>
          <p className='text-sm text-gray-500'>{t('gameDetails.goalsConceded')}</p>
        </div>
      </div>

      <div className='mb-12'>
        <SavesList
          gameId={id!}
          onSaveAdded={() => {}}
          onSaveDeleted={() => {}}
        />
      </div>

      {/* Video Analysis Section */}
      <div className='bg-white shadow rounded-lg p-6 mb-8'>
        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3'>
          <div>
            <h2 className='text-xl font-bold'>
              <span className='whitespace-nowrap'>{t('gameDetails.aiVideoAnalysis')} <Sparkles className='h-5 w-5 text-blue-500 inline ml-1' /></span>
            </h2>
            <div className='mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md'>
              <p className='text-sm text-amber-800 flex items-center gap-2'>
                <AlertCircle className='h-4 w-4 text-amber-600 flex-shrink-0' />
                <span className='font-medium'>Beta Version:</span>
                <span>{t('gameDetails.betaDisclaimer')}</span>
              </p>
            </div>
          </div>
          <div className='flex bg-gray-100 rounded-lg p-1 sm:ml-4 shrink-0'>
            <button
              onClick={() => {
                setVideoInputMethod('url');
                setVideoFile(null);
                setVideoError('');
              }}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors flex-1 sm:flex-none ${
                videoInputMethod === 'url' 
                  ? 'bg-white shadow-sm text-[#1b3968] font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              disabled={videoAnalyzing}
            >
              {t('game.labels.url')}
            </button>
            <button
              onClick={() => {
                setVideoInputMethod('file');
                setVideoUrl('');
                setVideoError('');
              }}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors flex-1 sm:flex-none ${
                videoInputMethod === 'file' 
                  ? 'bg-white shadow-sm text-[#1b3968] font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              disabled={videoAnalyzing}
            >
              {t('game.labels.upload')}
            </button>
          </div>
        </div>
        
        <div className='space-y-4'>
          {/* Video Input */}
          {videoInputMethod === 'url' ? (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('game.labels.videoUrl')}
              </label>
              <input
                type='url'
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder={t('gameDetails.videoUrlPlaceholder')}
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                disabled={videoAnalyzing}
              />
              <p className='text-xs text-gray-500 mt-1'>
                {t('game.labels.videoUrlRequirement')}
              </p>
            </div>
          ) : (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Upload Video File
              </label>
              <input
                type='file'
                accept='video/*'
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                className='block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#1b3968] file:text-white hover:file:bg-[#1b3968]/90'
                disabled={videoAnalyzing}
              />
              <p className='text-xs text-gray-500 mt-1'>
                {t('notifications.maxFileSize50MB')}
              </p>
            </div>
          )}

          {videoError && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
              <p className='text-red-700 text-sm'>{videoError}</p>
            </div>
          )}

          {/* Analyze Button */}
          <button
            onClick={handleVideoAnalysis}
            disabled={(!videoUrl && !videoFile) || videoUploading || videoAnalyzing}
            className='w-full bg-[#1b3968] text-white py-3 px-4 rounded-md hover:bg-[#1b3968]/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
          >
            {videoUploading ? (
              <>
                <Upload className='h-5 w-5 animate-pulse' />
                {t('goalForm.uploading')}
              </>
            ) : videoAnalyzing ? (
              <>
                <Loader className='h-5 w-5 animate-spin' />
                {t('game.labels.submittingToAI')}
              </>
            ) : (
              <>
                <Video className='h-5 w-5' />
                {t('gameDetails.analyzeVideoForGoals')}
              </>
            )}
          </button>

          {videoAnalyzing && (
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
              <p className='text-blue-700 text-sm'>
                {t('messages.aiAnalyzingVideo')}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6'>
        <h2 className='text-xl font-bold'>{t('gameDetails.goalsAnalysis')}</h2>
        <button
          onClick={() => {
            setEditingGoal(null);
            setFormData({
              title: '',
              xPosition: 50,
              yPosition: 50,
              shotXPosition: null,
              shotYPosition: null,
              shotType: '' as ShotType,
              gameSituation: '' as GameSituation,
              situationDescription: '',
              result: 'goal',
              status: 'completed',
              clipStartTime: '',
              clipEndTime: '',
              analysis: undefined,
              videoUrl: '',
              isAIGenerated: false,
            });
            setShowGoalForm(true);
          }}
          className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2 mt-2 sm:mt-0'
        >
          <PlusCircle className='h-5 w-5' />
          <span>{t('game.addGoal')}</span>
        </button>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        <div>
          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <h2 className='text-xl font-bold mb-6'>{t('gameDetails.goalLocation')}</h2>
            <GoalDiagram
              goals={goals}
              onPositionSelect={handleGoalPositionSelect}
              selectedGoal={editingGoal}
              provisionalPosition={provisionalPosition}
              readOnly={!showGoalForm}
            />
          </div>

          <div className='bg-white shadow rounded-lg p-6'>
            <h2 className='text-xl font-bold mb-6'>{t('gameDetails.shotLocation')}</h2>
            <ShotPositionDiagram
              goals={goals}
              onPositionSelect={handleShotPositionSelect}
              selectedGoal={editingGoal}
              provisionalPosition={provisionalShotPosition}
              readOnly={!showGoalForm}
            />
          </div>
        </div>

        <div>
          <div className='bg-white shadow rounded-lg p-6'>
            <h2 className='text-xl font-bold mb-6'>{t('gameDetails.goals')}</h2>
            {goals.length === 0 ? (
              <div className='text-center py-8'>
                <p className='text-gray-500 mb-4'>{t('game.noGoals')}</p>
                <button
                  onClick={() => {
                    setEditingGoal(null);
                    setFormData({
                      title: '',
                      xPosition: 50,
                      yPosition: 50,
                      shotXPosition: null,
                      shotYPosition: null,
                      shotType: '' as ShotType,
                      gameSituation: '' as GameSituation,
                      situationDescription: '',
                    });
                    setShowGoalForm(true);
                  }}
                  className='text-[#1b3968] hover:text-[#1b3968]/80 flex items-center gap-2 mx-auto'
                >
                  <PlusCircle className='h-5 w-5' />
                  {t('game.addFirstGoal')}
                </button>
              </div>
            ) : (
              <div className='space-y-4'>
                {goals.map((goal, index) => (
                  <div
                    key={goal.id}
                    className='bg-gray-50 rounded-lg p-3 border border-gray-200'
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center flex-1'>
                        <div className='flex-shrink-0 bg-[#1b3968] text-white rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2'>
                          {index + 1}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <h4 className='font-medium text-gray-900 truncate'>
                            {goal.title}
                          </h4>
                          <div className='mt-1'>
                            {/* Badges row */}
                            <div className='flex items-center gap-2 mb-1'>
                              {goal.isAIGenerated && (
                                <span className='inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'>
                                  <Sparkles className='h-2.5 w-2.5 mr-1' />
                                  AI
                                </span>
                              )}
                              {goal.result && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  goal.result === 'goal' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {goal.result.toUpperCase()}
                                </span>
                              )}
                            </div>
                            {/* Shot details row */}
                            <div className='flex items-center gap-2 text-xs flex-wrap'>
                              {goal.shotType && (
                                <span className='text-gray-600 truncate'>
                                  {goal.shotType}
                                </span>
                              )}
                              {goal.gameSituation && (
                                <span className='text-gray-500 truncate'>
                                  {goal.gameSituation}
                                </span>
                              )}
                            </div>
                            {/* Goal description */}
                            {goal.situationDescription && (
                              <p className='text-xs text-gray-600 mt-1 line-clamp-2'>
                                {goal.situationDescription}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='flex items-center space-x-3 ml-2'>
                        <button
                          onClick={() => handleEditGoal(goal)}
                          className='text-gray-400 hover:text-[#1b3968] p-1'
                          title={t('gameDetails.editGoal')}
                        >
                          <Pencil className='h-3.5 w-3.5' />
                        </button>
                        <button
                          onClick={() => setGoalToDelete(goal)}
                          className='text-gray-400 hover:text-red-500 p-1'
                          title={t('gameDetails.deleteGoal')}
                        >
                          <Trash2 className='h-3.5 w-3.5' />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showGoalForm && (
        <div 
          className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'
          onClick={() => {
            setShowGoalForm(false);
            setEditingGoal(null);
            setProvisionalPosition(null);
            setProvisionalShotPosition(null);
          }}
        >
          <div 
            className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2 className='text-xl font-bold'>
                  {editingGoal ? t('goalForm.editTitle') : t('goalForm.title')}
                </h2>
                <button
                  onClick={() => {
                    setShowGoalForm(false);
                    setEditingGoal(null);
                    setProvisionalPosition(null);
                    setProvisionalShotPosition(null);
                  }}
                  className='text-gray-400 hover:text-gray-500'
                >
                  <X className='h-6 w-6' />
                </button>
              </div>


              {/* AI Pre-filled Notice */}
              {formData.isAIGenerated && (formData.shotXPosition !== null || formData.xPosition !== null) && (
                <div className='mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md'>
                  <p className='text-sm text-blue-700'>
                    {t('game.labels.aiPrefilledMessage')}
                  </p>
                </div>
              )}

              {/* Shot Location */}
              <div className='mb-6'>
                <h3 className='text-lg font-medium mb-4'>Shot Location</h3>
                <div className='bg-white rounded-lg'>
                  <ShotPositionDiagram
                    goals={goals}
                    onPositionSelect={handleShotPositionSelect}
                    selectedGoal={editingGoal}
                    provisionalPosition={provisionalShotPosition}
                    readOnly={false}
                  />
                </div>
              </div>

              {/* Goal Location */}
              <div className='mb-6'>
                <h3 className='text-lg font-medium mb-4'>{t('game.labels.goalLocation')}</h3>
                <div className='bg-white rounded-lg'>
                  <GoalDiagram
                    goals={goals}
                    onPositionSelect={handleGoalPositionSelect}
                    selectedGoal={editingGoal}
                    provisionalPosition={provisionalPosition}
                    readOnly={false}
                  />
                </div>
              </div>

              <form onSubmit={handleGoalSubmit} className='space-y-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('goalForm.goalTitle')}
                  </label>
                  <input
                    type='text'
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className='block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                    placeholder={t('gameDetails.goalTitlePlaceholder')}
                    required
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {t('goalForm.shotType')}
                    </label>
                    
                    {/* Desktop: Keep the select dropdown */}
                    <select
                      value={formData.shotType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shotType: e.target.value as ShotType,
                        })
                      }
                      className='hidden md:block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                      required
                    >
                      <option value=''>{t('gameDetails.selectShotType')}</option>
                      <option value='Wrist Shot'>{t('goalForm.shotTypes.Wrist Shot')}</option>
                      <option value='Slap Shot'>{t('goalForm.shotTypes.Slap Shot')}</option>
                      <option value='Snap Shot'>{t('goalForm.shotTypes.Snap Shot')}</option>
                      <option value='Backhand'>{t('goalForm.shotTypes.Backhand')}</option>
                      <option value='Deflection / Tip-in'>
                        {t('goalForm.shotTypes.Deflection / Tip-in')}
                      </option>
                      <option value='One-Timer'>{t('goalForm.shotTypes.One-Timer')}</option>
                      <option value='Wraparound'>{t('goalForm.shotTypes.Wraparound')}</option>
                      <option value='Rebound Shot'>{t('goalForm.shotTypes.Rebound Shot')}</option>
                    </select>

                    {/* Mobile: Button that opens modal */}
                    <button
                      type="button"
                      onClick={() => setShowShotTypeModal(true)}
                      className='md:hidden flex items-center justify-between w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                    >
                      <span className='text-gray-700'>
                        {formData.shotType ? t(`goalForm.shotTypes.${formData.shotType}`) : t('gameDetails.selectShotType')}
                      </span>
                      <ChevronDown className='h-5 w-5 text-gray-400' />
                    </button>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {t('goalForm.gameSituation')}
                    </label>
                    
                    {/* Desktop: Keep the select dropdown */}
                    <select
                      value={formData.gameSituation}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gameSituation: e.target.value as GameSituation,
                        })
                      }
                      className='hidden md:block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                      required
                    >
                      <option value=''>{t('gameDetails.selectGameSituation')}</option>
                      <option value='Break away'>{t('goalForm.gameSituations.Break away')}</option>
                      <option value='Penalty Shot'>{t('goalForm.gameSituations.Penalty Shot')}</option>
                      <option value='Screened Shot'>{t('goalForm.gameSituations.Screened Shot')}</option>
                      <option value='Odd-Man Rush'>{t('goalForm.gameSituations.Odd-Man Rush')}</option>
                      <option value='Cross-Ice Pass'>{t('goalForm.gameSituations.Cross-Ice Pass')}</option>
                      <option value='Point Shot'>{t('goalForm.gameSituations.Point Shot')}</option>
                      <option value='Net-Front Battle'>{t('goalForm.gameSituations.Net-Front Battle')}</option>
                      <option value='Shorthanded Goal'>{t('goalForm.gameSituations.Shorthanded Goal')}</option>
                      <option value='Power Play Goal'>{t('goalForm.gameSituations.Power Play Goal')}</option>
                      <option value='Turnover Leading to Goal'>
                        {t('goalForm.gameSituations.Turnover Leading to Goal')}
                      </option>
                      <option value='Off the Rush'>{t('goalForm.gameSituations.Off the Rush')}</option>
                      <option value='Puck Battle / Scramble'>
                        {t('goalForm.gameSituations.Puck Battle / Scramble')}
                      </option>
                    </select>

                    {/* Mobile: Button that opens modal */}
                    <button
                      type="button"
                      onClick={() => setShowGameSituationModal(true)}
                      className='md:hidden flex items-center justify-between w-full px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                    >
                      <span className='text-gray-700'>
                        {formData.gameSituation ? t(`goalForm.gameSituations.${formData.gameSituation}`) : t('gameDetails.selectGameSituation')}
                      </span>
                      <ChevronDown className='h-5 w-5 text-gray-400' />
                    </button>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    {t('game.labels.situationDescription')}
                  </label>
                  <textarea
                    value={formData.situationDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        situationDescription: e.target.value,
                      })
                    }
                    className='block w-full rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                    rows={4}
                    placeholder={t('gameDetails.situationDescriptionPlaceholder')}
                    required
                  />
                </div>

                {/* AI Analysis Section - shown at bottom for AI-generated goals */}
                {formData.isAIGenerated && formData.analysis && (
                  <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6'>
                    <button
                      type='button'
                      onClick={() => setShowAIAnalytics(!showAIAnalytics)}
                      className='flex items-center justify-between w-full mb-4 text-left'
                    >
                      <div className='flex items-center'>
                        <Sparkles className='h-5 w-5 text-blue-600 mr-2' />
                        <h4 className='text-lg font-semibold text-blue-900'>{t('game.labels.aiGoalieAnalysis')}</h4>
                      </div>
                      {showAIAnalytics ? (
                        <ChevronUp className='h-5 w-5 text-blue-600' />
                      ) : (
                        <ChevronDown className='h-5 w-5 text-blue-600' />
                      )}
                    </button>
                    
                    {showAIAnalytics && (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                          {/* Goalie Technique */}
                          <div className='bg-white rounded-md p-4 border border-blue-100'>
                            <div className='flex items-center mb-2'>
                              <div className='h-3 w-3 bg-blue-500 rounded-full mr-2'></div>
                              <h5 className='font-semibold text-gray-900'>{t('game.labels.goalieTechnique')}</h5>
                            </div>
                            <p className='text-sm text-gray-700'>{formData.analysis.goalieTechnique}</p>
                          </div>

                          {/* What Went Well */}
                          <div className='bg-white rounded-md p-4 border border-green-100'>
                            <div className='flex items-center mb-2'>
                              <div className='h-3 w-3 bg-green-500 rounded-full mr-2'></div>
                              <h5 className='font-semibold text-gray-900'>{t('gameDetails.whatWentWell')}</h5>
                            </div>
                            <p className='text-sm text-gray-700'>{formData.analysis.whatWentWell}</p>
                          </div>

                          {/* Areas for Improvement */}
                          <div className='bg-white rounded-md p-4 border border-amber-100'>
                            <div className='flex items-center mb-2'>
                              <div className='h-3 w-3 bg-amber-500 rounded-full mr-2'></div>
                              <h5 className='font-semibold text-gray-900'>{t('gameDetails.areasForImprovement')}</h5>
                            </div>
                            <p className='text-sm text-gray-700'>{formData.analysis.areasForImprovement}</p>
                          </div>

                          {/* Key Moment */}
                          <div className='bg-white rounded-md p-4 border border-purple-100'>
                            <div className='flex items-center mb-2'>
                              <div className='h-3 w-3 bg-purple-500 rounded-full mr-2'></div>
                              <h5 className='font-semibold text-gray-900'>{t('gameDetails.keyMoment')}</h5>
                            </div>
                            <p className='text-sm text-gray-700'>{formData.analysis.keyMoment}</p>
                          </div>
                        </div>

                        {/* Coaching Tip - Full Width */}
                        <div className='bg-white rounded-md p-4 border border-indigo-100'>
                          <div className='flex items-center mb-2'>
                            <div className='h-3 w-3 bg-indigo-500 rounded-full mr-2'></div>
                            <h5 className='font-semibold text-gray-900'>{t('gameDetails.coachingTip')}</h5>
                          </div>
                          <p className='text-sm text-gray-700 font-medium'>{formData.analysis.coachingTip}</p>
                        </div>

                        {/* Video Clip Info */}
                        {formData.clipStartTime && formData.clipEndTime && (
                          <div className='bg-white rounded-md p-3 border border-gray-200'>
                            <div className='flex items-center justify-between mb-3'>
                              <div 
                                className='flex items-center flex-1 cursor-pointer hover:text-blue-600 transition-colors'
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (formData.videoUrl) {
                                    showClip(formData.clipStartTime!, formData.clipEndTime!);
                                  }
                                }}
                              >
                                <Play className='h-4 w-4 text-gray-600 mr-2' />
                                <span className='text-sm text-gray-700'>
                                  Video clip: {formData.clipStartTime} - {formData.clipEndTime}
                                </span>
                                {formData.videoUrl && (
                                  <span className='text-blue-600 text-sm font-medium ml-2'>{t('gameDetails.viewClip')}</span>
                                )}
                              </div>
                              {formData.videoUrl && (
                                <button
                                  type='button'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowVideoPlayer(!showVideoPlayer);
                                  }}
                                  className='text-gray-600 hover:text-gray-800 p-1'
                                >
                                  {showVideoPlayer ? (
                                    <ChevronUp className='h-4 w-4' />
                                  ) : (
                                    <ChevronDown className='h-4 w-4' />
                                  )}
                                </button>
                              )}
                            </div>
                            
                            {/* Expandable Video Player */}
                            {showVideoPlayer && formData.videoUrl && (
                              <div className='mt-3'>
                                <VideoPlayer 
                                  ref={videoPlayerRef}
                                  videoUrl={formData.videoUrl}
                                  className="w-full h-48 rounded-lg"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    disabled={goalSaving}
                    className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
                  >
                    {goalSaving ? (
                      <>
                        <Loader className='h-4 w-4 animate-spin' />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Save className='h-4 w-4' />
                        {editingGoal ? t('common.save') : t('game.addGoal')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {goalToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-md w-full p-6'>
            <h2 className='text-xl font-bold mb-4'>{t('gameDetails.deleteGoal')}</h2>
            <p className='text-gray-600 mb-6'>
              {t('messages.confirmDeleteGoal')}
            </p>
            <div className='flex justify-end gap-4'>
              <button
                onClick={() => setGoalToDelete(null)}
                disabled={deletingGoal}
                className='text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDeleteGoal(goalToDelete.id)}
                disabled={deletingGoal}
                className='bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
              >
                {deletingGoal ? (
                  <>
                    <Loader className='h-4 w-4 animate-spin' />
                    {t('common.deleting')}
                  </>
                ) : (
                  t('gameDetails.deleteGoal')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteGameModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-md w-full p-6'>
            <h2 className='text-xl font-bold mb-4'>{t('gameDetails.deleteGame')}</h2>
            <p className='text-gray-600 mb-6'>
              {t('messages.confirmDeleteGame')}
            </p>
            <div className='flex justify-end gap-4'>
              <button
                onClick={() => setShowDeleteGameModal(false)}
                className='text-gray-600 hover:text-gray-900'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteGame}
                className='bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600'
              >
                Delete Game
              </button>
            </div>
          </div>
        </div>
      )}

      {showShotsCounter && (
        <LiveShotsCounter
          game={game}
          onClose={() => setShowShotsCounter(false)}
          onSave={handleSaveShots}
        />
      )}

      {showShareDialog && (
        <ShareDialog
          gameIds={[id!]}
          onClose={() => setShowShareDialog(false)}
          isOpen={showShareDialog}
        />
      )}

      {/* Shot Type Modal for Mobile */}
      {showShotTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 md:hidden">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {t('goalForm.shotType')}
              </h2>
              <button
                onClick={() => setShowShotTypeModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { value: 'Wrist Shot', label: t('goalForm.shotTypes.Wrist Shot') },
                { value: 'Slap Shot', label: t('goalForm.shotTypes.Slap Shot') },
                { value: 'Snap Shot', label: t('goalForm.shotTypes.Snap Shot') },
                { value: 'Backhand', label: t('goalForm.shotTypes.Backhand') },
                { value: 'Deflection / Tip-in', label: t('goalForm.shotTypes.Deflection / Tip-in') },
                { value: 'One-Timer', label: t('goalForm.shotTypes.One-Timer') },
                { value: 'Wraparound', label: t('goalForm.shotTypes.Wraparound') },
                { value: 'Rebound Shot', label: t('goalForm.shotTypes.Rebound Shot') },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFormData({ ...formData, shotType: option.value as ShotType });
                    setShowShotTypeModal(false);
                  }}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                    formData.shotType === option.value
                      ? 'border-[#1b3968] bg-blue-50 text-[#1b3968]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-left font-medium">{option.label}</span>
                  {formData.shotType === option.value && <Check className="h-5 w-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Game Situation Modal for Mobile */}
      {showGameSituationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 md:hidden">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {t('goalForm.gameSituation')}
              </h2>
              <button
                onClick={() => setShowGameSituationModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { value: 'Break away', label: t('goalForm.gameSituations.Break away') },
                { value: 'Penalty Shot', label: t('goalForm.gameSituations.Penalty Shot') },
                { value: 'Screened Shot', label: t('goalForm.gameSituations.Screened Shot') },
                { value: 'Odd-Man Rush', label: t('goalForm.gameSituations.Odd-Man Rush') },
                { value: 'Cross-Ice Pass', label: t('goalForm.gameSituations.Cross-Ice Pass') },
                { value: 'Point Shot', label: t('goalForm.gameSituations.Point Shot') },
                { value: 'Net-Front Battle', label: t('goalForm.gameSituations.Net-Front Battle') },
                { value: 'Shorthanded Goal', label: t('goalForm.gameSituations.Shorthanded Goal') },
                { value: 'Power Play Goal', label: t('goalForm.gameSituations.Power Play Goal') },
                { value: 'Turnover Leading to Goal', label: t('goalForm.gameSituations.Turnover Leading to Goal') },
                { value: 'Off the Rush', label: t('goalForm.gameSituations.Off the Rush') },
                { value: 'Puck Battle / Scramble', label: t('goalForm.gameSituations.Puck Battle / Scramble') },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFormData({ ...formData, gameSituation: option.value as GameSituation });
                    setShowGameSituationModal(false);
                  }}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                    formData.gameSituation === option.value
                      ? 'border-[#1b3968] bg-blue-50 text-[#1b3968]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-left font-medium">{option.label}</span>
                  {formData.gameSituation === option.value && <Check className="h-5 w-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Snackbar Notification */}
      {showSnackbar && (
        <div className='fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2'>
          <Sparkles className='h-5 w-5' />
          <span>{snackbarMessage}</span>
        </div>
      )}
    </div>
  );
}